import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import CardinalCompass from "@/components/quote/CardinalCompass";
import LimeSpinner from "@/components/quote/LimeSpinner";
import { compassRotationDegrees } from "@/utils/compassHeading";
import { API_BASE } from "@/constants/api";
import { DEFAULT_LAYOUT_CONFIG } from "@/utils/panelLayoutCalculator";
import {
  boundaryWorldRings,
  satelliteGroundRect,
  type MeshGroundFrame,
  type MeshWorldCenter,
  type SatelliteGroundRect,
} from "@/utils/satelliteGroundPlane";
import { staticMapUrlWithoutMarkers } from "@/utils/solarLayoutOverlay";
import {
  layoutsForMesh,
  prepareRoofMesh,
} from "@/utils/roofMeshScene";
import {
  isRoofFacingNormal,
  panelRows,
  clipRememberedVisibility,
  rotatePanelRow,
  seedPriorityFromShown,
  snapPanelBesideGrid,
  translatePanelRow,
  visiblePanelIndexSet,
  type PanelMount,
  type PlacedRoofPanel,
  type RoofMeshLayout,
  type SnappedRoofPanel,
} from "@/utils/roofMeshPanelPlacement";
import { hydrateLayoutFromSession, savePanelLayoutSession } from "@/utils/panelLayoutSession";

export { clearRoofMeshLayoutCache } from "@/utils/roofMeshScene";

interface RoofMesh3DViewerProps {
  meshUrl: string | null;
  satelliteImageUrl?: string | null;
  groundFrame?: MeshGroundFrame | null;
  propertyBoundary?: { type?: string; coordinates?: unknown } | null;
  className?: string;
  width?: number;
  height?: number;
  onLayout?: (layout: RoofMeshLayout) => void;
  onFittedCount?: (count: number) => void;
  /** Draw this many modules from the start of the fitted layout. */
  visiblePanelCount?: number | null;
  /** Visible module count after a click places, reveals, or removes a panel. */
  onPlacedPanelCount?: (count: number) => void;
  onAlign?: (align: MeshWorldCenter) => void;
  /** Mesh URL is still being requested. */
  pending?: boolean;
  /** Bump to restore the automatic layout after reset or cache clear. */
  layoutRevision?: number;
}

/**
 * Three.js viewer for displaying 3D roof mesh from DSM GeoTIFF.
 * 
 * Displays GLB mesh with:
 * - Orbit controls (click & drag to rotate, scroll to zoom)
 * - Auto-rotation when not interacting
 * - Loading and error states
 * - Camera reset button
 */
export default function RoofMesh3DViewer({
  meshUrl,
  satelliteImageUrl = null,
  groundFrame = null,
  propertyBoundary = null,
  className = "",
  width = 800,
  height = 600,
  onLayout,
  onFittedCount,
  visiblePanelCount,
  onPlacedPanelCount,
  onAlign,
  pending = false,
  layoutRevision = 0,
}: RoofMesh3DViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const meshObjectRef = useRef<THREE.Object3D | null>(null);
  const panelsGroupRef = useRef<THREE.Group | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  
  const [layout, setLayout] = useState<RoofMeshLayout | null>(null);
  const [removedCount, setRemovedCount] = useState(0);
  const [selectedPanels, setSelectedPanels] = useState<number[]>([]);
  const [mount, setMount] = useState<PanelMount>("portrait");
  const [showHeightPoints, setShowHeightPoints] = useState(false);
  const fullLayoutRef = useRef<RoofMeshLayout | null>(null);
  const removedRef = useRef<number[]>([]);
  const onLayoutRef = useRef(onLayout);
  onLayoutRef.current = onLayout;
  const onFittedCountRef = useRef(onFittedCount);
  onFittedCountRef.current = onFittedCount;
  const visibleCountRef = useRef(visiblePanelCount);
  visibleCountRef.current = visiblePanelCount;
  // Package size before removals. A roof edit echoes a new total, and that echo
  // must not become a second limit on top of the panels already taken off.
  const packageLimitRef = useRef(visiblePanelCount);
  const onPlacedPanelCountRef = useRef(onPlacedPanelCount);
  onPlacedPanelCountRef.current = onPlacedPanelCount;
  const pinnedRef = useRef<number[]>([]);
  const arrangementByMountRef = useRef<Partial<Record<PanelMount, ArrangementEntry>>>({});
  const meshCacheKeyRef = useRef<string | null>(null);
  const meshUrlRef = useRef(meshUrl);
  meshUrlRef.current = meshUrl;
  const placementDrivenCountRef = useRef<number | null>(null);
  const ghostRef = useRef<THREE.Mesh | null>(null);
  const onAlignRef = useRef(onAlign);
  onAlignRef.current = onAlign;
  const layoutsRef = useRef<Partial<Record<PanelMount, RoofMeshLayout>>>({});
  const mountRef = useRef<PanelMount>("portrait");
  const selectedPanelsRef = useRef<number[]>([]);
  const activePanelRef = useRef<number | null>(null);
  const alignGroupRef = useRef<THREE.Group | null>(null);
  const satelliteRef = useRef(satelliteImageUrl);
  const frameRef = useRef(groundFrame);
  const boundaryRef = useRef(propertyBoundary);
  satelliteRef.current = satelliteImageUrl;
  frameRef.current = groundFrame;
  boundaryRef.current = propertyBoundary;
  selectedPanelsRef.current = selectedPanels;
  mountRef.current = mount;
  const [meshEpoch, setMeshEpoch] = useState(0);
  const groundAlignRef = useRef<{ x: number; z: number; baseY: number; shiftX: number } | null>(null);
  const [status, setStatus] = useState<"loading" | "loaded" | "error">("loading");
  const [error, setError] = useState<string>("");
  const autoRotateRef = useRef(false);
  const compassRoseRef = useRef<HTMLDivElement>(null);

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current || !meshUrl) return;

    const container = containerRef.current;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xd7e6f2);
    sceneRef.current = scene;

    const sky = createSkyDome();
    scene.add(sky);

    // Camera
    const camera = new THREE.PerspectiveCamera(
      50,
      width / height,
      0.1,
      1000
    );
    camera.position.set(30, 30, 30);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight1.position.set(10, 10, 5);
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight2.position.set(-5, 5, -5);
    scene.add(directionalLight2);

    // Hemisphere light for better color
    const hemiLight = new THREE.HemisphereLight(0xd6e6f2, 0xd8e6d8, 0.35);
    scene.add(hemiLight);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = false;
    controls.minDistance = 5;
    controls.maxDistance = 200;
    controls.maxPolarAngle = Math.PI / 2;
    controlsRef.current = controls;

    const stopAutoRotate = () => {
      autoRotateRef.current = false;
    };
    const updateCompass = () => {
      const rose = compassRoseRef.current;
      if (!rose) return;
      rose.style.transform = `rotate(${compassRotationDegrees(camera)}deg)`;
    };
    controls.addEventListener("end", stopAutoRotate);
    controls.addEventListener("change", updateCompass);

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let pointerDown: { x: number; y: number; shiftKey: boolean; ctrlKey: boolean } | null = null;
    let lastPanelClick = { index: -1, at: 0 };
    let alignDrag: {
      normal: THREE.Vector3;
      center: THREE.Vector3;
      startOffset: THREE.Vector3;
      base: RoofMeshLayout;
      indices: number[];
    } | null = null;

    const ghost = createPanelMesh({
      position: { x: 0, y: 0, z: 0 },
      normal: { x: 0, y: 1, z: 0 },
      across: { x: 1, y: 0, z: 0 },
      down: { x: 0, y: 0, z: 1 },
      width: DEFAULT_LAYOUT_CONFIG.panelWidth,
      height: DEFAULT_LAYOUT_CONFIG.panelHeight,
      facetIndex: 0,
    }, -1, true);
    scene.add(ghost);
    ghostRef.current = ghost;

    const pointerToNdc = (event: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const hideGhost = () => {
      ghost.visible = false;
    };

    const resolveRoofSnap = (): SnappedRoofPanel | null => {
      const full = fullLayoutRef.current;
      const roof = meshObjectRef.current;
      if (!full || !roof) return null;
      const targets = roofMeshes(roof);
      if (targets.length === 0) return null;
      const hit = raycaster.intersectObjects(targets, false)[0];
      if (!hit) return null;
      const normal = worldFaceNormal(hit);
      if (!normal || !isRoofFacingNormal(normal)) return null;
      const shown = visiblePanelIndexSet(
        full,
        packageLimitRef.current,
        removedRef.current,
        pinnedRef.current,
      );
      const snapped = snapPanelBesideGrid(
        { x: hit.point.x, y: hit.point.y, z: hit.point.z },
        { x: normal.x, y: normal.y, z: normal.z },
        full.panels,
        shown,
        undefined,
        full.minPanelY ?? -Infinity,
      );
      if (!snapped || !panelSitsOnRoof(snapped.panel, raycaster, targets)) return null;
      return snapped;
    };

    const commitSnap = (snapped: SnappedRoofPanel) => {
      const full = fullLayoutRef.current;
      const group = panelsGroupRef.current;
      if (!full || !group) return;
      markDirtyBeforeEdit(
        arrangementByMountRef,
        mountRef.current,
        full,
        packageLimitRef.current,
        removedRef.current,
        pinnedRef.current,
      );
      let touchedIndex: number;
      if (snapped.existingIndex != null) {
        const index = snapped.existingIndex;
        touchedIndex = index;
        removedRef.current = removedRef.current.filter((entry) => entry !== index);
        if (!pinnedRef.current.includes(index)) pinnedRef.current = [...pinnedRef.current, index];
        const mesh = group.children.find((child) => child.userData.panelIndex === index);
        if (mesh) mesh.visible = true;
      } else {
        const next = appendPanel(full, snapped.panel);
        const index = next.panels.length - 1;
        touchedIndex = index;
        fullLayoutRef.current = next;
        layoutsRef.current[mountRef.current] = next;
        group.add(createPanelMesh(snapped.panel, index));
        pinnedRef.current = [...pinnedRef.current, index];
      }
      hideGhost();
      const layout = fullLayoutRef.current;
      if (!layout) return;
      noteArrangementEdit(
        arrangementByMountRef,
        mountRef.current,
        layout,
        meshUrlRef.current,
        removedRef.current,
        touchedIndex,
      );
      const count = publishVisibleLayout(
        layout,
        removedRef.current,
        setLayout,
        setRemovedCount,
        onLayoutRef,
        packageLimitRef.current,
        pinnedRef.current,
      );
      reportVisiblePanelCount(count, placementDrivenCountRef, onPlacedPanelCountRef);
    };

    const hidePanel = (panelIndex: number) => {
      const group = panelsGroupRef.current;
      const full = fullLayoutRef.current;
      if (!group || !full || removedRef.current.includes(panelIndex)) return;
      markDirtyBeforeEdit(
        arrangementByMountRef,
        mountRef.current,
        full,
        packageLimitRef.current,
        removedRef.current,
        pinnedRef.current,
      );
      const mesh = group.children.find((child) => child.userData.panelIndex === panelIndex);
      if (mesh) mesh.visible = false;
      removedRef.current = [...removedRef.current, panelIndex];
      if (activePanelRef.current === panelIndex) activePanelRef.current = null;
      setSelectedPanels(selectedPanelsRef.current.filter((index) => index !== panelIndex));
      noteArrangementEdit(
        arrangementByMountRef,
        mountRef.current,
        full,
        meshUrlRef.current,
        removedRef.current,
      );
      const count = publishVisibleLayout(
        full,
        removedRef.current,
        setLayout,
        setRemovedCount,
        onLayoutRef,
        packageLimitRef.current,
        pinnedRef.current,
      );
      reportVisiblePanelCount(count, placementDrivenCountRef, onPlacedPanelCountRef);
    };

    const onPointerDown = (event: PointerEvent) => {
      const ctrlKey = event.ctrlKey || event.metaKey;
      pointerDown = { x: event.clientX, y: event.clientY, shiftKey: event.shiftKey, ctrlKey };
      if (ctrlKey) {
        controls.enabled = false;
        return;
      }
      const align = alignGroupRef.current;
      const camera = cameraRef.current;
      const full = fullLayoutRef.current;
      const indices = selectedPanelsRef.current;
      if (!align || !camera || !full || indices.length === 0) return;
      pointerToNdc(event);
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObjects(align.children, false)[0];
      if (!hit) return;
      const center = rowCenter(full.panels, indices);
      const normal = new THREE.Vector3(
        full.panels[indices[0]].normal.x,
        full.panels[indices[0]].normal.y,
        full.panels[indices[0]].normal.z,
      ).normalize();
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, center);
      const point = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(plane, point)) return;
      markDirtyBeforeEdit(
        arrangementByMountRef,
        mountRef.current,
        full,
        packageLimitRef.current,
        removedRef.current,
        pinnedRef.current,
      );
      alignDrag = {
        normal,
        center,
        startOffset: point.sub(center),
        base: cloneLayout(full),
        indices,
      };
      controls.enabled = false;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      if (target instanceof HTMLElement && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      const step = arrowStep(event.key);
      if (!step) return;
      const full = fullLayoutRef.current;
      const group = panelsGroupRef.current;
      const indices = selectedPanelsRef.current.filter((index) => !removedRef.current.includes(index));
      if (!full || !group || indices.length === 0) return;
      event.preventDefault();
      markDirtyBeforeEdit(
        arrangementByMountRef,
        mountRef.current,
        full,
        packageLimitRef.current,
        removedRef.current,
        pinnedRef.current,
      );
      const distance = event.shiftKey ? 0.5 : 0.1;
      const nextPanels = translatePanelRow(full.panels, indices, step.across * distance, step.down * distance);
      const next: RoofMeshLayout = { ...full, panels: nextPanels };
      fullLayoutRef.current = next;
      layoutsRef.current[mountRef.current] = next;
      for (const index of indices) {
        const mesh = group.children.find((child) => child.userData.panelIndex === index);
        if (mesh) posePanel(mesh, next.panels[index]);
      }
      const align = alignGroupRef.current;
      const seed = next.panels[indices[0]];
      if (align) {
        align.position.add(new THREE.Vector3(
          seed.across.x * step.across * distance + seed.down.x * step.down * distance,
          seed.across.y * step.across * distance + seed.down.y * step.down * distance,
          seed.across.z * step.across * distance + seed.down.z * step.down * distance,
        ));
      }
      publishVisibleLayout(next, removedRef.current, setLayout, setRemovedCount, onLayoutRef, packageLimitRef.current, pinnedRef.current);
      noteArrangementEdit(
        arrangementByMountRef,
        mountRef.current,
        next,
        meshUrlRef.current,
        removedRef.current,
      );
    };
    const onPointerMove = (event: PointerEvent) => {
      const drag = alignDrag;
      const camera = cameraRef.current;
      const group = panelsGroupRef.current;
      if (!drag || !camera || !group) {
        if (pointerDown || !camera || !group) hideGhost();
        else {
          pointerToNdc(event);
          const rect = renderer.domElement.getBoundingClientRect();
          const inside = event.clientX >= rect.left && event.clientX <= rect.right
            && event.clientY >= rect.top && event.clientY <= rect.bottom;
          if (!inside || !cameraRef.current) hideGhost();
          else {
            raycaster.setFromCamera(pointer, cameraRef.current);
            const snapped = resolveRoofSnap();
            if (snapped) poseGhost(ghost, snapped.panel);
            else hideGhost();
          }
        }
        return;
      }
      hideGhost();
      pointerToNdc(event);
      raycaster.setFromCamera(pointer, camera);
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(drag.normal, drag.center);
      const point = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(plane, point)) return;
      const current = point.sub(drag.center);
      const turned = new THREE.Vector3().crossVectors(drag.startOffset, current);
      const radians = Math.atan2(turned.dot(drag.normal), drag.startOffset.dot(current));
      const next: RoofMeshLayout = {
        ...drag.base,
        panels: rotatePanelRow(drag.base.panels, drag.indices, radians),
      };
      fullLayoutRef.current = next;
      layoutsRef.current[mountRef.current] = next;
      for (const index of drag.indices) {
        const mesh = group.children.find((child) => child.userData.panelIndex === index);
        if (mesh) posePanel(mesh, next.panels[index]);
      }
    };
    const onPointerUp = (event: PointerEvent) => {
      const start = pointerDown;
      pointerDown = null;
      if (alignDrag) {
        alignDrag = null;
        controls.enabled = true;
        if (fullLayoutRef.current) {
          publishVisibleLayout(
            fullLayoutRef.current,
            removedRef.current,
            setLayout,
            setRemovedCount,
            onLayoutRef,
            packageLimitRef.current,
            pinnedRef.current,
          );
          noteArrangementEdit(
            arrangementByMountRef,
            mountRef.current,
            fullLayoutRef.current,
            meshUrlRef.current,
            removedRef.current,
          );
        }
        return;
      }
      if (!start || !panelsGroupRef.current || !cameraRef.current) return;
      if (start.ctrlKey) controls.enabled = true;
      const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
      if (moved > 5) return;

      pointerToNdc(event);
      raycaster.setFromCamera(pointer, cameraRef.current);
      const hit = raycaster.intersectObjects(
        panelsGroupRef.current.children.filter((child) => child.visible),
        false,
      )[0];
      const panelIndex = hit?.object.userData.panelIndex;
      if (start.ctrlKey) {
        if (typeof panelIndex === "number") hidePanel(panelIndex);
        return;
      }
      if (typeof panelIndex !== "number" || removedRef.current.includes(panelIndex) || !fullLayoutRef.current) {
        if (!start.shiftKey) {
          setSelectedPanels([]);
          const snapped = resolveRoofSnap();
          if (snapped) commitSnap(snapped);
        }
        return;
      }
      const row = panelRows(fullLayoutRef.current.panels).find((entry) => entry.includes(panelIndex));
      if (!row) return;
      const now = performance.now();
      const isolated = lastPanelClick.index === panelIndex && now - lastPanelClick.at < 400;
      lastPanelClick = { index: panelIndex, at: now };
      if (isolated && !start.shiftKey) {
        activePanelRef.current = panelIndex;
        setSelectedPanels([panelIndex]);
        return;
      }
      const current = selectedPanelsRef.current;
      if (start.shiftKey && current.length > 0) {
        const anchor = fullLayoutRef.current.panels[current[0]];
        const added = fullLayoutRef.current.panels[row[0]];
        const aligned = added.normal.x * anchor.normal.x
          + added.normal.y * anchor.normal.y
          + added.normal.z * anchor.normal.z;
        const sameFacet = added.facetIndex === anchor.facetIndex && aligned >= 0.98;
        if (!sameFacet) return;
        const inRow = new Set(row);
        const already = row.every((index) => current.includes(index));
        activePanelRef.current = panelIndex;
        setSelectedPanels(already
          ? current.filter((index) => !inRow.has(index))
          : [...current, ...row.filter((index) => !current.includes(index))]);
        return;
      }
      activePanelRef.current = panelIndex;
      setSelectedPanels(row);
    };
    const onContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };
    renderer.domElement.addEventListener("pointerdown", onPointerDown, true);
    renderer.domElement.addEventListener("pointerup", onPointerUp);
    renderer.domElement.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("keydown", onKeyDown);

    // Load GLTF. Orbit events must not change React state: that used to
    // re-run this effect and fetch the GLB again on mouse up.
    setStatus("loading");
    setError("");
    setLayout(null);

    const loader = new GLTFLoader();
    loader.load(
      meshUrl,
      (gltf) => {
        const mesh = gltf.scene;
        mesh.traverse((child) => {
          const material = (child as THREE.Mesh).material;
          const materials = Array.isArray(material) ? material : material ? [material] : [];
          materials.forEach((entry) => {
            entry.side = THREE.DoubleSide;
          });
        });
        meshObjectRef.current = mesh;
        const prepared = prepareRoofMesh(mesh, {
          groundFrame: frameRef.current,
          propertyBoundary: boundaryRef.current,
          satelliteImageUrl: satelliteRef.current,
          cacheKey: meshUrl,
        });

        scene.add(mesh);
        const heightPoints = createHeightPoints(mesh);
        if (heightPoints) scene.add(heightPoints);
        groundAlignRef.current = prepared.align;
        meshCacheKeyRef.current = prepared.cacheKey;
        layoutsRef.current = layoutsForMesh(prepared.cacheKey, mesh);
        arrangementByMountRef.current = {};
        onAlignRef.current?.({ x: prepared.align.x, z: prepared.align.z });
        setMeshEpoch((epoch) => epoch + 1);

        // Adjust camera to fit mesh
        const fitted = new THREE.Box3().setFromObject(mesh);
        const size = fitted.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);
        let cameraDistance = Math.abs(maxDim / Math.sin(fov / 2));
        cameraDistance *= 1.5; // Add some padding

        camera.position.set(
          cameraDistance * 0.3,
          cameraDistance * 0.3,
          cameraDistance * 0.3
        );
        camera.lookAt(0, 0, 0);
        controls.target.set(0, 0, 0);
        controls.update();

        setStatus("loaded");
      },
      (progress) => {
        // Progress callback (optional)
        const percent = (progress.loaded / progress.total) * 100;
        console.log(`Loading mesh: ${percent.toFixed(0)}%`);
      },
      (err) => {
        console.error("Error loading mesh:", err);
        setError("Failed to load 3D mesh");
        setStatus("error");
      }
    );

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      if (autoRotateRef.current && meshObjectRef.current) {
        meshObjectRef.current.rotation.y += 0.003;
      }

      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      controls.removeEventListener("end", stopAutoRotate);
      controls.removeEventListener("change", updateCompass);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown, true);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("keydown", onKeyDown);
      controls.dispose();
      
      if (meshObjectRef.current) {
        scene.remove(meshObjectRef.current);
      }

      if (ghostRef.current) {
        scene.remove(ghostRef.current);
        ghostRef.current.geometry.dispose();
        const ghostMaterial = ghostRef.current.material;
        if (Array.isArray(ghostMaterial)) ghostMaterial.forEach((entry) => entry.dispose());
        else ghostMaterial.dispose();
        ghostRef.current = null;
      }
      
      if (panelsGroupRef.current) {
        panelsGroupRef.current.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose();
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
        scene.remove(panelsGroupRef.current);
      }
      
      scene.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        if (child.name !== "sky" && child.name !== "ground") return;
        child.geometry.dispose();
        const material = child.material;
        const materials = Array.isArray(material) ? material : [material];
        materials.forEach((entry) => {
          if (entry.map) entry.map.dispose();
          entry.dispose();
        });
      });

      removeNamedObject(scene, "boundary");
      removeNamedObject(scene, "height-points");
      renderer.dispose();
      
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [meshUrl]);

  useEffect(() => {
    const scene = sceneRef.current;
    const align = groundAlignRef.current;
    if (!scene || !align || meshEpoch === 0) return;

    removeNamedMesh(scene, "ground");
    const rect = satelliteImageUrl && groundFrame
      ? satelliteGroundRect(satelliteImageUrl, groundFrame, { x: align.x, z: align.z })
      : null;
    const ground = rect
      ? createSatelliteGround(rect, align.baseY)
      : createGroundPlane(align.baseY);
    scene.add(ground);
    removeNamedObject(scene, "boundary");
    if (groundFrame && propertyBoundary) {
      const shiftX = align.shiftX ?? 0;
      const rings = boundaryWorldRings(propertyBoundary, groundFrame, { x: align.x, z: align.z })
        .map((ring) => ring.map((point) => ({ x: point.x + shiftX, z: point.z })));
      const boundary = createBoundaryLine(rings, align.baseY);
      if (boundary) scene.add(boundary);
    }

    if (!rect || !satelliteImageUrl) return;

    let cancelled = false;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    const textureUrl = `${API_BASE}/api/property/satellite-texture?url=${encodeURIComponent(staticMapUrlWithoutMarkers(satelliteImageUrl))}`;
    loader.load(textureUrl, (texture) => {
      if (cancelled || ground.parent !== scene) {
        texture.dispose();
        return;
      }
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.center.set(0.5, 0.5);
      texture.repeat.set(-1, -1);
      const material = ground.material as THREE.MeshStandardMaterial;
      material.map = texture;
      material.color.set(0xffffff);
      material.needsUpdate = true;
    });

    return () => {
      cancelled = true;
    };
  }, [meshEpoch, satelliteImageUrl, groundFrame, propertyBoundary]);

  useEffect(() => {
    const points = sceneRef.current?.getObjectByName("height-points");
    if (points) points.visible = showHeightPoints;
  }, [showHeightPoints, meshEpoch]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || meshEpoch === 0) return;

    if (panelsGroupRef.current) {
      disposePanels(panelsGroupRef.current);
      scene.remove(panelsGroupRef.current);
      panelsGroupRef.current = null;
    }

    const baseline = layoutsRef.current[mount];
    if (!baseline) return;
    const remembered = applyRememberedLayout(
      meshUrlRef.current,
      mount,
      baseline,
      visibleCountRef.current,
      layoutsRef,
      arrangementByMountRef,
    );
    const roofLayout = remembered?.layout ?? baseline;
    fullLayoutRef.current = roofLayout;
    activePanelRef.current = null;
    setSelectedPanels([]);
    onFittedCountRef.current?.(roofLayout.panels.length);

    let shown: Set<number>;
    if (remembered) {
      removedRef.current = remembered.removed;
      pinnedRef.current = remembered.shown;
      packageLimitRef.current = 0;
      shown = new Set(remembered.shown);
    } else {
      if (!arrangementByMountRef.current[mount]) {
        arrangementByMountRef.current[mount] = { removed: [], priority: [], shown: [], dirty: false };
      }
      removedRef.current = [];
      pinnedRef.current = [];
      packageLimitRef.current = visibleCountRef.current;
      shown = visiblePanelIndexSet(roofLayout, packageLimitRef.current, [], []);
    }

    publishVisibleLayout(
      roofLayout,
      removedRef.current,
      setLayout,
      setRemovedCount,
      onLayoutRef,
      packageLimitRef.current,
      pinnedRef.current,
      remembered ? remembered.shown : undefined,
    );

    mountPanelGroup(scene, panelsGroupRef, roofLayout, shown);
  }, [meshEpoch, mount]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || meshEpoch === 0) return;
    if (placementDrivenCountRef.current === visiblePanelCount) {
      placementDrivenCountRef.current = null;
      return;
    }
    placementDrivenCountRef.current = null;
    activePanelRef.current = null;
    setSelectedPanels([]);
    if (ghostRef.current) ghostRef.current.visible = false;

    const mountNow = mountRef.current;
    const baseline = layoutsRef.current[mountNow] ?? fullLayoutRef.current;
    const remembered = baseline
      ? applyRememberedLayout(
        meshUrlRef.current,
        mountNow,
        baseline,
        visiblePanelCount,
        layoutsRef,
        arrangementByMountRef,
      )
      : null;

    if (!remembered) {
      const full = fullLayoutRef.current;
      const group = panelsGroupRef.current;
      if (!full || !group) return;
      packageLimitRef.current = visiblePanelCount;
      removedRef.current = [];
      pinnedRef.current = [];
      const shown = visiblePanelIndexSet(full, visiblePanelCount);
      group.children.forEach((child) => {
        const index = child.userData.panelIndex;
        if (typeof index !== "number" || !full.panels[index]) return;
        posePanel(child, full.panels[index]);
        child.visible = shown.has(index);
      });
      onFittedCountRef.current?.(full.panels.length);
      publishVisibleLayout(full, [], setLayout, setRemovedCount, onLayoutRef, visiblePanelCount, []);
      return;
    }

    const full = remembered.layout;
    fullLayoutRef.current = full;
    removedRef.current = remembered.removed;
    pinnedRef.current = remembered.shown;
    packageLimitRef.current = 0;
    const shown = new Set(remembered.shown);
    mountPanelGroup(scene, panelsGroupRef, full, shown);
    onFittedCountRef.current?.(full.panels.length);
    publishVisibleLayout(
      full,
      remembered.removed,
      setLayout,
      setRemovedCount,
      onLayoutRef,
      0,
      remembered.shown,
      remembered.shown,
    );
    if (meshUrlRef.current) {
      savePanelLayoutSession(meshUrlRef.current, mountNow, full, {
        dirty: true,
        removed: remembered.removed,
        priority: remembered.priority,
        shown: remembered.shown,
      });
    }
  }, [visiblePanelCount, meshEpoch]);

  const appliedLayoutRevisionRef = useRef(layoutRevision);
  useEffect(() => {
    if (appliedLayoutRevisionRef.current === layoutRevision) return;
    appliedLayoutRevisionRef.current = layoutRevision;
    if (layoutRevision === 0) return;
    const mesh = meshObjectRef.current;
    const cacheKey = meshCacheKeyRef.current;
    if (!mesh || !cacheKey) return;
    layoutsRef.current = layoutsForMesh(cacheKey, mesh);
    arrangementByMountRef.current = {};
    setMeshEpoch((epoch) => epoch + 1);
  }, [layoutRevision]);

  useEffect(() => {
    const scene = sceneRef.current;
    const full = fullLayoutRef.current;
    const group = panelsGroupRef.current;
    clearAlign(scene, alignGroupRef.current);
    alignGroupRef.current = null;
    restorePanelMaterials(group);
    const indices = selectedPanels.filter((index) => !removedRef.current.includes(index));
    if (!scene || !full || !group || indices.length === 0) return;
    for (const index of indices) {
      const mesh = group.children.find((child) => child.userData.panelIndex === index);
      const material = mesh instanceof THREE.Mesh ? mesh.material : null;
      if (material instanceof THREE.MeshStandardMaterial) {
        material.emissive.set(0x88aa00);
        material.emissiveIntensity = 0.45;
      }
    }

    const seed = full.panels[indices[0]];
    const center = rowCenter(full.panels, indices);
    const normal = new THREE.Vector3(seed.normal.x, seed.normal.y, seed.normal.z).normalize();
    const across = new THREE.Vector3(seed.across.x, seed.across.y, seed.across.z).normalize();
    const down = new THREE.Vector3().crossVectors(across, normal).normalize();
    let radius = 0.5;
    for (const index of indices) {
      const panel = full.panels[index];
      const offset = new THREE.Vector3(
        panel.position.x - center.x,
        panel.position.y - center.y,
        panel.position.z - center.z,
      );
      const reach = Math.hypot(panel.width, panel.height) / 2;
      radius = Math.max(radius, Math.hypot(offset.dot(across), offset.dot(down)) + reach);
    }
    radius += 0.18;

    const align = new THREE.Group();
    align.position.copy(center);
    align.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(across, down, normal));
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(radius, 0.035, 8, 64),
      new THREE.MeshBasicMaterial({ color: 0xd4ff00 }),
    );
    const grab = new THREE.Mesh(
      new THREE.TorusGeometry(radius, 0.16, 8, 32),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
    );
    align.add(ring, grab);
    alignGroupRef.current = align;
    scene.add(align);

    return () => {
      clearAlign(scene, align);
      if (alignGroupRef.current === align) alignGroupRef.current = null;
      restorePanelMaterials(panelsGroupRef.current);
    };
  }, [selectedPanels, meshEpoch, mount]);

  const removeSelected = () => {
    const panelIndex = activePanelRef.current;
    const full = fullLayoutRef.current;
    const group = panelsGroupRef.current;
    if (panelIndex === null || !full || !group || removedRef.current.includes(panelIndex)) return;
    markDirtyBeforeEdit(
      arrangementByMountRef,
      mountRef.current,
      full,
      packageLimitRef.current,
      removedRef.current,
      pinnedRef.current,
    );
    const mesh = group.children.find((child) => child.userData.panelIndex === panelIndex);
    if (mesh) mesh.visible = false;
    removedRef.current = [...removedRef.current, panelIndex];
    activePanelRef.current = null;
    setSelectedPanels([]);
    noteArrangementEdit(
      arrangementByMountRef,
      mountRef.current,
      full,
      meshUrlRef.current,
      removedRef.current,
    );
    const count = publishVisibleLayout(
      full,
      removedRef.current,
      setLayout,
      setRemovedCount,
      onLayoutRef,
      packageLimitRef.current,
      pinnedRef.current,
    );
    reportVisiblePanelCount(count, placementDrivenCountRef, onPlacedPanelCountRef);
  };

  const undoRemove = () => {
    const panelIndex = removedRef.current.at(-1);
    const full = fullLayoutRef.current;
    const group = panelsGroupRef.current;
    if (panelIndex === undefined || !full || !group) return;
    markDirtyBeforeEdit(
      arrangementByMountRef,
      mountRef.current,
      full,
      packageLimitRef.current,
      removedRef.current,
      pinnedRef.current,
    );
    removedRef.current = removedRef.current.slice(0, -1);
    const mesh = group.children.find((child) => child.userData.panelIndex === panelIndex);
    if (mesh) {
      mesh.visible = visiblePanelIndexSet(full, packageLimitRef.current, removedRef.current, pinnedRef.current).has(panelIndex);
    }
    noteArrangementEdit(
      arrangementByMountRef,
      mountRef.current,
      full,
      meshUrlRef.current,
      removedRef.current,
      panelIndex,
    );
    const count = publishVisibleLayout(
      full,
      removedRef.current,
      setLayout,
      setRemovedCount,
      onLayoutRef,
      packageLimitRef.current,
      pinnedRef.current,
    );
    reportVisiblePanelCount(count, placementDrivenCountRef, onPlacedPanelCountRef);
  };

  useEffect(() => {
    if (status !== "loaded") return;
    const rose = compassRoseRef.current;
    const camera = cameraRef.current;
    if (!rose || !camera) return;
    rose.style.transform = `rotate(${compassRotationDegrees(camera)}deg)`;
  }, [status]);

  // Reset camera
  const resetCamera = () => {
    if (!cameraRef.current || !controlsRef.current) return;

    const camera = cameraRef.current;
    const controls = controlsRef.current;

    // Reset to initial position
    camera.position.set(30, 30, 30);
    camera.lookAt(0, 0, 0);
    controls.target.set(0, 0, 0);
    controls.update();
  };

  if (!meshUrl) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-3 bg-[#0b1220] rounded-lg ${className}`}
        style={{ width, height }}
      >
        {pending ? (
          <>
            <span className="flex items-center justify-center rounded-full bg-white p-3">
              <LimeSpinner size="lg" color="current" className="text-brand-navy" decorative />
            </span>
            <p className="text-sm text-white/70">Loading 3D mesh…</p>
          </>
        ) : (
          <p className="text-sm text-white/70">No 3D mesh available</p>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div
        ref={containerRef}
        className="rounded-lg overflow-hidden border border-[rgba(17,17,17,0.08)]"
        style={{ width, height }}
      />

      {status === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0b1220]/90 rounded-lg">
          <span className="flex items-center justify-center rounded-full bg-white p-3">
            <LimeSpinner size="lg" color="current" className="text-brand-navy" decorative />
          </span>
          <p className="text-sm text-white/70">Loading 3D mesh…</p>
        </div>
      )}

      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0b1220]/90 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
          <p className="text-xs text-white/50">
            The 3D mesh could not be loaded
          </p>
        </div>
      )}

      {status === "loaded" && (
        <>
          <CardinalCompass
            roseRef={compassRoseRef}
            label="Compass. N points north as you rotate the view."
          />
          <button
            onClick={resetCamera}
            className="absolute top-3 right-3 rounded-md bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm hover:bg-black/80 transition-colors"
          >
            Reset View
          </button>

          <button
            type="button"
            onClick={removeSelected}
            disabled={selectedPanels.length === 0}
            className="absolute bottom-24 right-3 rounded-md bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm hover:bg-black/80 transition-colors disabled:cursor-not-allowed disabled:text-white/30"
          >
            Remove
          </button>

          <button
            type="button"
            onClick={undoRemove}
            disabled={removedCount === 0}
            className="absolute bottom-14 right-3 rounded-md bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm hover:bg-black/80 transition-colors disabled:cursor-not-allowed disabled:text-white/30"
          >
            Undo
          </button>

          <button
            type="button"
            onClick={() => setShowHeightPoints((visible) => !visible)}
            className={`absolute bottom-14 left-3 rounded-md px-3 py-1.5 text-xs font-semibold backdrop-blur-sm transition-colors ${showHeightPoints ? "bg-white text-black" : "bg-black/60 text-white hover:bg-black/80"}`}
          >
            Height map
          </button>

          <div className="absolute bottom-3 right-3 flex overflow-hidden rounded-md bg-black/60 text-xs font-semibold text-white backdrop-blur-sm">
            <button
              type="button"
              onClick={() => setMount("portrait")}
              className={`px-3 py-1.5 transition-colors ${mount === "portrait" ? "bg-white text-black" : "text-white/80 hover:bg-black/80"}`}
            >
              Portrait
            </button>
            <button
              type="button"
              onClick={() => setMount("landscape")}
              className={`px-3 py-1.5 transition-colors ${mount === "landscape" ? "bg-white text-black" : "text-white/80 hover:bg-black/80"}`}
            >
              Landscape
            </button>
          </div>

          <div className="absolute bottom-3 left-3 rounded-md bg-black/60 px-2.5 py-1 text-xs text-white backdrop-blur-sm">
            {layout ? (
              <div>
                <p className="font-semibold">Solar Panel Layout</p>
                <p className="text-white/80">{layout.panels.length} panels • {layout.totalKwp.toFixed(1)} kWp</p>
              </div>
            ) : (
              <p>3D Building Model</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function createSkyDome(): THREE.Mesh {
  const canvas = document.createElement("canvas");
  canvas.width = 4;
  canvas.height = 512;
  const context = canvas.getContext("2d");
  if (context) {
    const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, "#8aa6c8");
    gradient.addColorStop(0.45, "#b4cfe6");
    gradient.addColorStop(0.78, "#d7e6f2");
    gradient.addColorStop(1, "#f3f7fa");
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sky = new THREE.Mesh(
    new THREE.SphereGeometry(480, 32, 24),
    new THREE.MeshBasicMaterial({ map: texture, side: THREE.BackSide, depthWrite: false }),
  );
  sky.name = "sky";
  return sky;
}

function createGroundPlane(baseY: number): THREE.Mesh {
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(240, 240),
    new THREE.MeshStandardMaterial({ color: 0xc5d6c6, roughness: 1, metalness: 0 }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = baseY - 0.04;
  ground.name = "ground";
  return ground;
}

function createSatelliteGround(rect: SatelliteGroundRect, baseY: number): THREE.Mesh {
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(rect.width, rect.depth),
    new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, metalness: 0 }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(rect.x, baseY - 0.04, rect.z);
  ground.name = "ground";
  return ground;
}

function createHeightPoints(root: THREE.Object3D): THREE.Points | null {
  const samples: number[] = [];
  const point = new THREE.Vector3();
  root.updateMatrixWorld(true);
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const position = mesh.geometry.getAttribute("position");
    if (!position) return;
    const step = position.count > 60000 ? Math.ceil(position.count / 30000) : 1;
    for (let index = 0; index < position.count; index += step) {
      point.fromBufferAttribute(position, index).applyMatrix4(mesh.matrixWorld);
      samples.push(point.x, point.y + 0.08, point.z);
    }
  });
  if (samples.length < 9) return null;

  let minY = Infinity;
  let maxY = -Infinity;
  for (let index = 1; index < samples.length; index += 3) {
    minY = Math.min(minY, samples[index]);
    maxY = Math.max(maxY, samples[index]);
  }
  const span = Math.max(0.001, maxY - minY);
  const colors = new Float32Array(samples.length);
  const low = new THREE.Color(0x1d4ed8);
  const high = new THREE.Color(0xdc2626);
  const color = new THREE.Color();
  for (let index = 1, colorIndex = 0; index < samples.length; index += 3, colorIndex += 3) {
    color.copy(low).lerp(high, (samples[index] - minY) / span);
    colors[colorIndex] = color.r;
    colors[colorIndex + 1] = color.g;
    colors[colorIndex + 2] = color.b;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(samples, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const points = new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      size: 3.5,
      vertexColors: true,
      sizeAttenuation: false,
    }),
  );
  points.name = "height-points";
  points.visible = false;
  return points;
}

function createBoundaryLine(
  rings: Array<Array<{ x: number; z: number }>>,
  baseY: number,
): THREE.Mesh | null {
  const positions: number[] = [];
  const y = baseY + 0.2;
  const half = 0.08;
  for (const ring of rings) {
    for (let index = 0; index < ring.length; index++) {
      const start = ring[index];
      const end = ring[(index + 1) % ring.length];
      const dx = end.x - start.x;
      const dz = end.z - start.z;
      const length = Math.hypot(dx, dz);
      if (length < 0.05) continue;
      const px = (-dz / length) * half;
      const pz = (dx / length) * half;
      const ax = start.x + px;
      const az = start.z + pz;
      const bx = start.x - px;
      const bz = start.z - pz;
      const cx = end.x + px;
      const cz = end.z + pz;
      const ex = end.x - px;
      const ez = end.z - pz;
      positions.push(ax, y, az, bx, y, bz, cx, y, cz, bx, y, bz, ex, y, ez, cx, y, cz);
    }
  }
  if (positions.length === 0) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  const line = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({ color: 0xdc2626, side: THREE.DoubleSide }),
  );
  line.name = "boundary";
  return line;
}

function removeNamedObject(scene: THREE.Scene, name: string) {
  const existing = scene.getObjectByName(name);
  if (!existing) return;
  existing.traverse((child) => {
    const geometry = (child as THREE.Mesh).geometry;
    if (geometry && typeof geometry.dispose === "function") geometry.dispose();
  });
  const seen = new Set<THREE.Material>();
  existing.traverse((child) => {
    const material = (child as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
    if (!material) return;
    const materials = Array.isArray(material) ? material : [material];
    materials.forEach((entry) => {
      if (seen.has(entry)) return;
      seen.add(entry);
      entry.dispose();
    });
  });
  scene.remove(existing);
}

function removeNamedMesh(scene: THREE.Scene, name: string) {
  const existing = scene.getObjectByName(name);
  if (!(existing instanceof THREE.Mesh)) return;
  scene.remove(existing);
  existing.geometry.dispose();
  const material = existing.material;
  const materials = Array.isArray(material) ? material : [material];
  materials.forEach((entry) => {
    if (entry.map) entry.map.dispose();
    entry.dispose();
  });
}

function reportVisiblePanelCount(
  count: number,
  placementDrivenCountRef: { current: number | null },
  onPlacedPanelCountRef: { current?: (count: number) => void },
) {
  // The parent echoes this count back as visiblePanelCount. Matching it here
  // keeps the removal instead of redrawing the package from the start.
  placementDrivenCountRef.current = count;
  onPlacedPanelCountRef.current?.(count);
}

function publishVisibleLayout(
  full: RoofMeshLayout,
  removed: number[],
  setLayout: (layout: RoofMeshLayout) => void,
  setRemovedCount: (count: number) => void,
  onLayoutRef: { current?: (layout: RoofMeshLayout) => void },
  limit?: number | null,
  pinned: readonly number[] = [],
  explicitShown?: Iterable<number>,
): number {
  const shown = explicitShown
    ? new Set(explicitShown)
    : visiblePanelIndexSet(full, limit, removed, pinned);
  const panels = full.panels.filter((_, index) => shown.has(index));
  const facetCounts = full.facetCounts.map((_, facet) => (
    panels.filter((panel) => panel.facetIndex === facet).length
  ));
  const visible: RoofMeshLayout = {
    panels,
    facetCounts,
    totalKwp: (panels.length * DEFAULT_LAYOUT_CONFIG.panelWatts) / 1000,
  };
  setLayout(visible);
  setRemovedCount(removed.length);
  onLayoutRef.current?.(visible);
  return shown.size;
}

function appendPanel(layout: RoofMeshLayout, panel: PlacedRoofPanel): RoofMeshLayout {
  const panels = [...layout.panels, panel];
  const facetCounts = layout.facetCounts.slice();
  while (facetCounts.length <= panel.facetIndex) facetCounts.push(0);
  facetCounts[panel.facetIndex] += 1;
  return {
    panels,
    facetCounts,
    totalKwp: (panels.length * DEFAULT_LAYOUT_CONFIG.panelWatts) / 1000,
  };
}

const PANEL_STROKE = 0xd4ff00;

function createPanelMesh(panel: PlacedRoofPanel, index: number, ghost = false): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(panel.width, 0.04, panel.height),
    new THREE.MeshStandardMaterial({
      color: ghost ? 0x8ea0b5 : 0x1a1a1a,
      metalness: 0.3,
      roughness: 0.7,
      transparent: ghost,
      opacity: ghost ? 0.45 : 1,
      depthWrite: !ghost,
    }),
  );
  posePanel(mesh, panel);
  mesh.userData.panelIndex = index;
  if (ghost) {
    mesh.name = "panel-ghost";
    mesh.visible = false;
    mesh.raycast = () => {};
  } else {
    mesh.add(createPanelStroke(panel.width, panel.height));
  }
  return mesh;
}

/** Lime face outline, matching the homepage example panels (`stroke="#d4ff00"`). */
function createPanelStroke(width: number, depth: number): THREE.Mesh {
  const thickness = Math.min(width, depth) * 0.045;
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const y = 0.021;
  const positions: number[] = [];
  const quad = (x0: number, z0: number, x1: number, z1: number) => {
    positions.push(
      x0, y, z0, x1, y, z0, x1, y, z1,
      x0, y, z0, x1, y, z1, x0, y, z1,
    );
  };
  quad(-halfWidth, -halfDepth, halfWidth, -halfDepth + thickness);
  quad(-halfWidth, halfDepth - thickness, halfWidth, halfDepth);
  quad(-halfWidth, -halfDepth + thickness, -halfWidth + thickness, halfDepth - thickness);
  quad(halfWidth - thickness, -halfDepth + thickness, halfWidth, halfDepth - thickness);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  const stroke = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({
      color: PANEL_STROKE,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
  );
  stroke.name = "panel-stroke";
  stroke.raycast = () => {};
  return stroke;
}

function poseGhost(mesh: THREE.Mesh, panel: PlacedRoofPanel) {
  const geometry = mesh.geometry;
  const params = geometry instanceof THREE.BoxGeometry ? geometry.parameters : null;
  if (!params || Math.abs(params.width - panel.width) > 1e-6 || Math.abs(params.depth - panel.height) > 1e-6) {
    geometry.dispose();
    mesh.geometry = new THREE.BoxGeometry(panel.width, 0.04, panel.height);
  }
  posePanel(mesh, panel);
  mesh.visible = true;
}

function roofMeshes(root: THREE.Object3D): THREE.Object3D[] {
  const meshes: THREE.Object3D[] = [];
  root.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) meshes.push(child);
  });
  return meshes;
}

function worldFaceNormal(hit: THREE.Intersection): THREE.Vector3 | null {
  if (!hit.face) return null;
  const normal = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize();
  if (normal.y < 0) normal.negate();
  return normal;
}

export function panelSitsOnRoof(
  panel: PlacedRoofPanel,
  raycaster: THREE.Raycaster,
  targets: THREE.Object3D[],
): boolean {
  if (targets.length === 0) return false;
  const normal = new THREE.Vector3(panel.normal.x, panel.normal.y, panel.normal.z).normalize();
  if (!isRoofFacingNormal(normal)) return false;
  const across = new THREE.Vector3(panel.across.x, panel.across.y, panel.across.z).normalize();
  const down = new THREE.Vector3().crossVectors(across, normal).normalize();
  const origin = new THREE.Vector3(panel.position.x, panel.position.y, panel.position.z);
  const direction = normal.clone().negate();
  const steps = 4;
  let hits = 0;
  const previousFar = raycaster.far;
  raycaster.far = 3;
  for (let row = 0; row < steps; row++) {
    for (let col = 0; col < steps; col++) {
      const u = ((col + 0.5) / steps - 0.5) * panel.width;
      const v = ((row + 0.5) / steps - 0.5) * panel.height;
      const from = origin.clone()
        .addScaledVector(across, u)
        .addScaledVector(down, v)
        .addScaledVector(normal, 1.5);
      raycaster.set(from, direction);
      const hit = raycaster.intersectObjects(targets, false)[0];
      if (!hit) continue;
      const faceNormal = worldFaceNormal(hit);
      if (!faceNormal || !isRoofFacingNormal(faceNormal) || faceNormal.dot(normal) < 0.94) continue;
      const lift = hit.point.clone().sub(origin).dot(normal);
      if (lift > 0.05 || lift < -0.55) continue;
      hits++;
    }
  }
  raycaster.far = previousFar;
  return hits / (steps * steps) >= 0.8;
}

function arrowStep(key: string): { across: number; down: number } | null {
  if (key === "ArrowLeft") return { across: -1, down: 0 };
  if (key === "ArrowRight") return { across: 1, down: 0 };
  if (key === "ArrowUp") return { across: 0, down: -1 };
  if (key === "ArrowDown") return { across: 0, down: 1 };
  return null;
}

function posePanel(mesh: THREE.Object3D, panel: PlacedRoofPanel) {
  const normal = new THREE.Vector3(panel.normal.x, panel.normal.y, panel.normal.z).normalize();
  const across = new THREE.Vector3(panel.across.x, panel.across.y, panel.across.z).normalize();
  const down = new THREE.Vector3().crossVectors(across, normal).normalize();
  mesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(across, normal, down));
  mesh.position.set(panel.position.x, panel.position.y, panel.position.z);
}

function rowCenter(panels: PlacedRoofPanel[], indices: number[]): THREE.Vector3 {
  const center = new THREE.Vector3();
  for (const index of indices) {
    const position = panels[index].position;
    center.x += position.x;
    center.y += position.y;
    center.z += position.z;
  }
  return center.multiplyScalar(1 / indices.length);
}

type ArrangementEntry = { removed: number[]; priority: number[]; shown: number[]; dirty: boolean };

function applyRememberedLayout(
  meshUrl: string | null,
  mount: PanelMount,
  baseline: RoofMeshLayout,
  limit: number | null | undefined,
  layoutsRef: { current: Partial<Record<PanelMount, RoofMeshLayout>> },
  store: { current: Partial<Record<PanelMount, ArrangementEntry>> },
): { layout: RoofMeshLayout; removed: number[]; priority: number[]; shown: number[] } | null {
  const inMemory = store.current[mount];
  const hydrated = !inMemory?.dirty && meshUrl
    ? hydrateLayoutFromSession(meshUrl, mount, baseline)
    : null;
  const sourceLayout = inMemory?.dirty ? (layoutsRef.current[mount] ?? baseline) : hydrated?.layout;
  const sourceState = inMemory?.dirty ? inMemory : hydrated?.state;
  if (!sourceLayout || !sourceState || !(inMemory?.dirty || hydrated?.state.dirty)) return null;
  const clipped = clipRememberedVisibility(sourceLayout.panels.length, limit, sourceState);
  layoutsRef.current[mount] = sourceLayout;
  store.current[mount] = {
    dirty: true,
    removed: clipped.removed,
    priority: clipped.priority,
    shown: clipped.shown,
  };
  return {
    layout: sourceLayout,
    removed: clipped.removed,
    priority: clipped.priority,
    shown: clipped.shown,
  };
}

function mountPanelGroup(
  scene: THREE.Scene,
  groupRef: { current: THREE.Group | null },
  layout: RoofMeshLayout,
  shown: Set<number>,
) {
  if (groupRef.current) {
    disposePanels(groupRef.current);
    scene.remove(groupRef.current);
  }
  const group = new THREE.Group();
  layout.panels.forEach((panel, index) => {
    const mesh = createPanelMesh(panel, index);
    mesh.visible = shown.has(index);
    group.add(mesh);
  });
  groupRef.current = group;
  scene.add(group);
}

function getArrangement(
  store: { current: Partial<Record<PanelMount, ArrangementEntry>> },
  mount: PanelMount,
): ArrangementEntry {
  return store.current[mount] ?? { removed: [], priority: [], shown: [], dirty: false };
}

function visibleNow(
  full: RoofMeshLayout,
  limit: number | null | undefined,
  removed: number[],
  pinned: number[],
): number[] {
  return [...visiblePanelIndexSet(full, limit, removed, pinned)];
}

function markDirtyBeforeEdit(
  store: { current: Partial<Record<PanelMount, ArrangementEntry>> },
  mount: PanelMount,
  full: RoofMeshLayout,
  packageLimit: number | null | undefined,
  removed: number[],
  pinned: number[],
): void {
  if (getArrangement(store, mount).dirty) return;
  const shown = visibleNow(full, packageLimit, removed, pinned);
  store.current[mount] = {
    dirty: true,
    removed: [...removed],
    shown,
    priority: seedPriorityFromShown(full, new Set(shown)),
  };
}

function noteArrangementEdit(
  store: { current: Partial<Record<PanelMount, ArrangementEntry>> },
  mount: PanelMount,
  layout: RoofMeshLayout,
  meshUrl: string | null,
  removed: number[],
  touchedIndex?: number,
): void {
  const current = getArrangement(store, mount);
  if (!current.dirty) return;
  const removedSet = new Set(removed);
  const shown = current.shown.filter((index) => !removedSet.has(index));
  if (touchedIndex != null && !removedSet.has(touchedIndex) && !shown.includes(touchedIndex)) {
    shown.push(touchedIndex);
  }
  const entry: ArrangementEntry = {
    dirty: true,
    removed: [...removed],
    shown,
    priority: [...shown, ...current.priority.filter((index) => !shown.includes(index) && !removedSet.has(index))],
  };
  store.current[mount] = entry;
  if (meshUrl) savePanelLayoutSession(meshUrl, mount, layout, entry);
}

function cloneLayout(layout: RoofMeshLayout): RoofMeshLayout {
  return {
    panels: layout.panels.map((panel) => ({
      ...panel,
      position: { ...panel.position },
      normal: { ...panel.normal },
      across: { ...panel.across },
      down: { ...panel.down },
    })),
    facetCounts: [...layout.facetCounts],
    totalKwp: layout.totalKwp,
  };
}

function restorePanelMaterials(group: THREE.Group | null) {
  group?.children.forEach((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const material = child.material;
    if (material instanceof THREE.MeshStandardMaterial) {
      material.emissive.set(0x000000);
      material.emissiveIntensity = 1;
    }
  });
}

function clearAlign(scene: THREE.Scene | null, align: THREE.Group | null) {
  if (!align) return;
  scene?.remove(align);
  align.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.geometry.dispose();
    const material = child.material;
    if (Array.isArray(material)) material.forEach((entry) => entry.dispose());
    else material.dispose();
  });
}

function disposePanels(group: THREE.Group) {
  group.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry.dispose();
      if (Array.isArray(child.material)) {
        child.material.forEach((mat) => mat.dispose());
      } else {
        child.material.dispose();
      }
    }
  });
}

